/**
 * Video times — the four moments of a match, read off its video.
 *
 * Opened in its own window from the Layers page (Game Facts, whether or
 * not the game has a stack record yet), so the video can be large and
 * scrubbed while the Layers page stays where it is. Play or step to a
 * moment, press "Mark" beside it, then Save.
 *
 * Before the stack record: saves to game_files through
 * POST /api/v1/ops/games/<game_file_id>/meta
 * — the platform-admin write that already validates the order (kickoff <=
 * first-half end <= second-half kickoff <= end). Layers Import reads these
 * columns. Only the marks that were changed are sent: the fields show
 * 0.1 s, so resending an untouched mark would round its stored value.
 *
 * Also the home team's first-half attacking direction, which Layers
 * Import refuses to guess (missing_direction): the same route writes it
 * to game_files and, through direction_sync, game_facts.home_attack_dir_h1
 * (source 'declared'). Sent only when it was changed, like the marks — a
 * vendor direction the lead import recorded stays 'vendor' until someone
 * deliberately changes it here.
 *
 * Once the game HAS a stack record, the four times go through the Game
 * Facts match clock instead (POST .../game-facts, match_clock), which
 * writes the stack's periods AND game_files — the meta route writes
 * game_files alone, and the two would disagree (game 214, 2026-10-16: a
 * full-time mark past the video's end, fixable only by typing seconds).
 * The match clock needs all four. The direction is then Game Facts'
 * (kept in L1), so it is not offered here.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import MatchMarks, {
  BLANK_MARKS, MARKS as MARK_KEYS, clockToSecs, marksProblem, secsToField,
  type MarkFields, type MarkKey,
} from '../components/MatchMarks'
import { ApiError, api } from '../lib/api'
import { VIDEO_TIMES_SAVED } from './videoTimesMessage'

export { orderProblem } from '../components/MatchMarks'

type Info = {
  game_code: string | null
  game_file_id: number | null
  fps: number | null
  video_game_start_time: number | null
  video_halftime_start_time: number | null
  video_halftime_end_time: number | null
  video_normaltime_end_time: number | null
  home_attack_dir_h1: 'left' | 'right' | null
}

type Direction = '' | 'left' | 'right'

/** The match clock's keys (ops_qa_api.MATCH_CLOCK_KEYS), mark by mark. */
const CLOCK_KEY: Record<MarkKey, string> = {
  kickoff_s: 'kickoff_s',
  h1_end_s: 'halftime_s',
  h2_start_s: 'second_half_s',
  game_end_s: 'fulltime_s',
}

type ClockSaved = {
  rebuild?: { queued: true; batch_id: string }
    | { queued: false; code: string; message: string }
}

/** What the match clock would refuse, or null: all four, kick-off before
 *  half-time and the second half's kick-off before full time. */
export function clockProblem(values: (number | null)[]): string | null {
  const [ko, ht, sh, ft] = values
  if (ko == null || ht == null || sh == null || ft == null) {
    return 'This game has a stack record: set all four times to save.'
  }
  return ko < ht && ht <= sh && sh < ft ? null
    : 'Kick off must be before first-half end, and second-half kick off before end.'
}

/** The info field each mark reads back. */
const INFO_FIELD: Record<MarkKey, keyof Info> = {
  kickoff_s: 'video_game_start_time',
  h1_end_s: 'video_halftime_start_time',
  h2_start_s: 'video_halftime_end_time',
  game_end_s: 'video_normaltime_end_time',
}
const MARKS = MARK_KEYS.map((m) => ({ ...m, info: INFO_FIELD[m.key] }))

export default function VideoTimes() {
  const { gameId } = useParams()
  const video = useRef<HTMLVideoElement>(null)
  const [info, setInfo] = useState<Info | null>(null)
  // undefined while loading, null when the game has no playable video
  const [src, setSrc] = useState<string | null | undefined>(undefined)
  const [fields, setFields] = useState<MarkFields>(BLANK_MARKS)
  // What was loaded (or last saved): a mark whose field still reads this
  // is not sent.
  const [loaded, setLoaded] = useState<MarkFields>(BLANK_MARKS)
  const [dir, setDir] = useState<Direction>('')
  const [loadedDir, setLoadedDir] = useState<Direction>('')
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [saving, setSaving] = useState(false)
  // Whether the game has a stack record (decides where Save writes);
  // undefined until known, and nothing is saved before it is.
  const [onStack, setOnStack] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    api<Info>(`/api/v1/opsqa/games/${gameId}/match-lab-info`)
      .then((d) => {
        setInfo(d)
        const f = Object.fromEntries(MARKS.map((m) =>
          [m.key, secsToField(d[m.info] as number | null)])) as MarkFields
        setFields(f)
        setLoaded(f)
        const d0: Direction = d.home_attack_dir_h1 ?? ''
        setDir(d0)
        setLoadedDir(d0)
      })
      .catch((e: unknown) => setMsg({ ok: false, text: e instanceof ApiError
        ? e.message : String(e) }))
    api<{ video_url: string | null; video_url_fallback?: string | null }>(
      `/api/v1/opsqa/games/${gameId}/video`)
      .then((d) => setSrc(d.video_url ?? d.video_url_fallback ?? null))
      .catch(() => setSrc(null))
    api<{ exists: boolean }>(`/api/v1/opsqa/games/${gameId}/game-facts`)
      .then((d) => setOnStack(!!d.exists))
      .catch((e: unknown) => setMsg({ ok: false, text: e instanceof ApiError
        ? e.message : String(e) }))
  }, [gameId])

  const getVideo = useCallback(() => video.current, [])
  const values = MARKS.map((m) => clockToSecs(fields[m.key]))
  const problem = marksProblem(fields) ?? (onStack ? clockProblem(values) : null)
  const changed: (readonly [string, number | string | null | undefined])[] = [
    ...MARKS.flatMap((m, i) =>
      fields[m.key] !== loaded[m.key] ? [[m.key, values[i]] as const] : []),
    // '' (not set) is never sent: clearing a known direction is not
    // something this window offers.
    ...(!onStack && dir !== loadedDir && dir !== ''
      ? [['direction', dir] as const] : []),
  ]

  const save = async () => {
    if (!info?.game_file_id || onStack === undefined) return
    setSaving(true); setMsg(null)
    try {
      let text = 'Saved. Import on the Layers page reads these.'
      if (onStack) {
        // All four, as the clock needs them; an untouched mark is sent as
        // it was loaded, not as its 0.1 s field (see above).
        const clock = Object.fromEntries(MARKS.map((m, i) => [CLOCK_KEY[m.key],
          fields[m.key] === loaded[m.key] && info[m.info] != null
            ? info[m.info] : values[i]]))
        const r = await api<ClockSaved>(`/api/v1/opsqa/games/${gameId}/game-facts`, {
          method: 'POST', body: JSON.stringify({ match_clock: clock }),
        })
        text = r.rebuild && !r.rebuild.queued
          ? `Saved, but the rebuild was NOT queued: ${r.rebuild.message}`
          : 'Saved: both halves and the game record. The Layers page reloads Game Facts.'
      } else {
        await api(`/api/v1/ops/games/${info.game_file_id}/meta`, {
          method: 'POST',
          body: JSON.stringify(Object.fromEntries(changed)),
        })
      }
      setLoaded(fields)
      setLoadedDir(dir)
      setMsg({ ok: true, text })
      window.opener?.postMessage({ type: VIDEO_TIMES_SAVED, gameId: Number(gameId) },
                                 window.location.origin)
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setSaving(false) }
  }

  return (
    <div style={{ padding: 'var(--space-3)' }}>
      <h1 className="page-title">Match timings · {info?.game_code ?? `game ${gameId}`}</h1>
      {info && !info.game_file_id && (
        <p className="adm-msg adm-msg--err">
          This game has no game_files row, so there is nowhere to save times.
        </p>
      )}
      {src ? (
        <video ref={video} src={src} controls preload="metadata"
               style={{ width: '100%', maxHeight: '62vh', background: '#000',
                        borderRadius: 4 }} />
      ) : (
        <p className="muted">{src === null ? 'No playable video for this game.'
          : 'Loading video…'}</p>
      )}
      <div style={{ marginTop: 'var(--space-2)' }}>
        <MatchMarks getVideo={getVideo} fps={info?.fps ?? null} fields={fields}
                    onChange={(k, v) => setFields((f) => ({ ...f, [k]: v }))} />
      </div>
      {onStack ? (
        <p className="muted t-data" style={{ marginTop: 'var(--space-3)' }}>
          The home team&rsquo;s first-half direction is set in Game Facts on
          the Layers page.
        </p>
      ) : (
      <div className="adm-bar" style={{ marginTop: 'var(--space-3)' }}>
        <label className="adm-field">
          <span className="t-label">Home team attacks, first half</span>
          <select className="adm-select" value={dir}
                  aria-label="Home team attacks, first half"
                  onChange={(e) => setDir(e.target.value as Direction)}>
            <option value="" disabled>not set — Import needs it</option>
            <option value="right">right</option>
            <option value="left">left</option>
          </select>
        </label>
        <span className="muted t-data" style={{ alignSelf: 'end' }}>
          Which way the home team attacks from kick off.
          {loadedDir && dir === loadedDir ? ' Already recorded.' : ''}
        </span>
      </div>
      )}
      {problem && <p className="adm-msg adm-msg--err">{problem}</p>}
      <div className="adm-actions">
        <button className="adm-btn" onClick={() => void save()}
                disabled={saving || !!problem || !info?.game_file_id
                          || onStack === undefined || changed.length === 0}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}
    </div>
  )
}
